/**
 * @file     PhoneSignInPanel.tsx
 * @purpose  "Sign in with a text code": mobile number, then the 6-digit code.
 *           Works for an account that has confirmed its phone; anyone else is
 *           pointed back to email. Puts the session in place on success and
 *           hands control back to the sign-in form's redirect.
 */
'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MuiLink from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { formatPhoneInput, toE164 } from '@/utils/formatPhone';

type Props = {
  onSignedIn: () => void;
  onBack: () => void;
  /** Shared field and button styles from SignInForm. */
  inputSx: object;
  btnSx: object;
  linkSx: object;
};

export function PhoneSignInPanel({ onSignedIn, onBack, inputSx, btnSx, linkSx }: Props) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const [phone, setPhone] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    const e164 = toE164(phone);
    if (!e164) {
      setError(t('auth.phone.invalid', { defaultValue: 'Enter a complete mobile number.' }));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await secureFetch('/api/auth/phone-sign-in/start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ phone: e164 }),
      });
      if (res.ok) {
        setSentTo(e164);
        setCode('');
        return;
      }
      setError(
        res.status === 429
          ? t('auth.phone.rateLimited', { defaultValue: 'Too many attempts. Wait a minute and try again.' })
          : t('auth.phone.sendFailed', { defaultValue: 'The code could not be sent. Try again in a moment.' }),
      );
    } catch {
      setError(t('auth.phone.sendFailed', { defaultValue: 'The code could not be sent. Try again in a moment.' }));
    } finally {
      setBusy(false);
    }
  };

  const check = async (value: string) => {
    if (!sentTo || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await secureFetch('/api/auth/phone-sign-in/check', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ phone: sentTo, code: value }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        session?: { access_token: string; refresh_token: string };
      };
      if (res.ok && json.ok) {
        if (json.session) {
          const { createSupabaseBrowserClient } = await import('@/services/supabase/client');
          await createSupabaseBrowserClient().auth.setSession(json.session);
        }
        onSignedIn();
        return;
      }
      setError(
        json.error === 'invalid_code'
          ? t('auth.phone.wrongCode', { defaultValue: 'That code did not match. Check the text and try again.' })
          : json.error === 'other_user_signed_in'
            ? t('auth.phone.otherUser', { defaultValue: 'Someone else is signed in on this device. Sign out first.' })
            : json.error === 'use_email'
              ? t('auth.phone.useEmail', { defaultValue: 'This number is not confirmed on an account. Sign in with your email instead.' })
              : t('auth.phone.checkFailed', { defaultValue: 'The code could not be checked. Try again in a moment.' }),
      );
    } catch {
      setError(t('auth.phone.checkFailed', { defaultValue: 'The code could not be checked. Try again in a moment.' }));
    } finally {
      setBusy(false);
    }
  };

  const onCode = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) void check(digits);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sentTo === null ? (
        <>
          <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary }}>
            {t('auth.phone.intro', { defaultValue: 'Enter the mobile number you confirmed on your account. We will text you a code.' })}
          </Typography>
          <TextField
            label={t('auth.phone.label', { defaultValue: 'Mobile number' })}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus
            value={phone}
            onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void send(); } }}
            fullWidth
            sx={inputSx}
          />
          <Button variant="contained" fullWidth disabled={busy} onClick={() => void send()} sx={btnSx}>
            {busy ? t('auth.phone.sending', { defaultValue: 'Sending...' }) : t('auth.phone.send', { defaultValue: 'Text me a code' })}
          </Button>
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary }}>
            {t('auth.phone.sent', {
              defaultValue: 'If {{phone}} is confirmed on an account, a 6-digit code is on its way.',
              phone: formatPhoneInput(sentTo.replace(/^\+1/, '')),
            })}
          </Typography>
          <TextField
            label={t('auth.phone.codeLabel', { defaultValue: '6-digit code' })}
            name="one-time-code"
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
            value={code}
            onChange={(e) => onCode(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 6 }, input: { endAdornment: busy ? <CircularProgress size={18} /> : undefined } }}
            fullWidth
            sx={{ ...inputSx, '& input': { letterSpacing: '0.35em', fontSize: 20 } }}
          />
          <MuiLink component="button" type="button" variant="body2" onClick={() => { setSentTo(null); setError(null); }} sx={{ ...linkSx, minHeight: 44, alignSelf: 'flex-start' }}>
            {t('auth.phone.changeNumber', { defaultValue: 'Use a different number' })}
          </MuiLink>
        </>
      )}
      {error && (
        <Typography role="alert" variant="body2" sx={{ color: theme.semanticColors.statusError }}>
          {error}
        </Typography>
      )}
      <MuiLink component="button" type="button" variant="body2" onClick={onBack} sx={{ ...linkSx, minHeight: 44 }}>
        {t('auth.phone.back', { defaultValue: 'Sign in with email instead' })}
      </MuiLink>
    </Box>
  );
}
